import type { Screen } from './types.js';

/**
 * Story #77 — the countdown on the presented screen.
 *
 * There is no scheduler in this system: every Lambda is request-driven and the
 * WebSocket API declares no `$default` route, so nothing can fire at zero. A
 * deadline is therefore **data**, not an event — written onto the quiz item by
 * the same update that moves `currentScreenIndex`, and compared by every reader
 * against a clock it already has.
 *
 * Which is why the deadline is an absolute instant rather than a
 * remaining-seconds number: seconds are already stale by the time they land on
 * a phone. The payload carries the server's own time beside it so a device with
 * a wrong clock counts down from a measured offset instead of its own `now`.
 *
 * Browser-safe by the rule in CLAUDE.md — the taker's phone runs the same
 * arithmetic the API enforces, so the room and the server agree on when zero
 * was.
 */

/** Below this a screen is unanswerable; above it, a clock is not what is wanted. */
export const MIN_SCREEN_TIME_LIMIT_SECONDS = 5;
export const MAX_SCREEN_TIME_LIMIT_SECONDS = 3600;

/**
 * How long past zero the server still accepts a vote.
 *
 * Covers a submission that left the phone before the deadline and landed after
 * it. Refusing those would punish the voter for latency they cannot see, and
 * the window is far too short to be worth gaming — the taker's buttons lock at
 * zero, so nothing in the UI offers it.
 */
export const VOTE_GRACE_MS = 2_000;

/** Whether a stored limit is one this system would have written. */
function isUsableLimit(value: unknown): value is number {
  return (
    typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= MIN_SCREEN_TIME_LIMIT_SECONDS &&
    value <= MAX_SCREEN_TIME_LIMIT_SECONDS
  );
}

/**
 * The instant a screen's clock runs out, or null when it has none.
 *
 * Bounds-checks the stored limit rather than trusting it: `validateScreens`
 * guards the authoring path, but this is what a deadline is computed from on
 * every advance, and a nonsense value should mean "no clock" rather than a
 * screen that expires in the year 2140.
 */
export function deadlineFor(
  screen: Pick<Screen, 'timeLimitSeconds'> | null,
  from: Date,
): string | null {
  if (!screen || !isUsableLimit(screen.timeLimitSeconds)) return null;
  return new Date(from.getTime() + screen.timeLimitSeconds * 1000).toISOString();
}

/**
 * Milliseconds left on the clock, floored at zero, or null when there is none.
 *
 * Takes `now` rather than reading it, so a phone can pass its own drift-corrected
 * time and the server can pass its own.
 */
export function msRemaining(deadlineAt: string | null | undefined, now: Date): number | null {
  if (!deadlineAt) return null;
  const deadline = Date.parse(deadlineAt);
  // An unparseable deadline reads as no clock rather than NaN, which would
  // otherwise compare false against everything and lock nothing.
  if (Number.isNaN(deadline)) return null;
  return Math.max(0, deadline - now.getTime());
}

/**
 * Whether the clock has run out, optionally allowing a grace window.
 *
 * The grace argument is for the server's vote check only. A client passes none:
 * a locked button is not a rejected request, and letting a phone offer a vote
 * into the grace window would show a control whose success depends on latency.
 */
export function isPastDeadline(
  deadlineAt: string | null | undefined,
  now: Date,
  graceMs = 0,
): boolean {
  const remaining = msRemaining(deadlineAt, now);
  if (remaining === null) return false;
  if (remaining > 0) return false;
  const deadline = Date.parse(deadlineAt as string);
  // `>=`, so a zero grace window makes the deadline itself the first late
  // instant rather than a millisecond after it.
  return now.getTime() - deadline >= graceMs;
}

/**
 * The one rule for "this question no longer takes answers".
 *
 * #49's presenter close and #77's expiry are two reasons, not two mechanisms:
 * every client renders the same locked state off this predicate, and the server
 * separates the two only in the error code it returns, so a phone can say "time
 * is up" rather than "the presenter closed it".
 */
export function votingHasClosed(
  state: { votingClosed?: boolean; screenDeadlineAt?: string | null },
  now: Date,
): boolean {
  if (state.votingClosed === true) return true;
  return isPastDeadline(state.screenDeadlineAt, now);
}
